import PropTypes from 'prop-types';
import React, { useState, useMemo, useLayoutEffect } from 'react';
import { connect } from 'react-redux';

import { addPageData, navigateWizard } from '../../actions';
import { persistUnsupportedSchoolsInterest } from '../../actions';
import { fetchCampusesRequest } from '../../actions/fetchCampusActions';
import Button from '../../components/Button';
import PageLayout from '../../components/PageLayout';
import SelectLoadingFetch from '../../components/SelectLoadingFetch';
import analytics from '../../lib/analytics';
import { track } from '../../lib/heapHelpers';
import useCampuses from '../../lib/hooks/useCampuses';
import useSchools from '../../lib/hooks/useSchools';
import * as payloadFactory from '../../lib/pageDataPayloadFactory';

const SchoolPage = ({
  dispatchAddPageData,
  dispatchWizardNav,
  dispatchFetchCampuses,
  dispatchUpdateUnsupportedSchoolsInterest,
}) => {
  const { selected } = useSchools();
  const { allCampuses: campuses } = useCampuses();
  const [selectedSchool, setSelectedSchool] = useState(selected);
  const [hasChanged, setHasChanged] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useLayoutEffect(() => {
    const { value: schoolId } = selectedSchool || {};
    if (schoolId && !campuses[schoolId]) {
      dispatchFetchCampuses(schoolId);
    }
  }, [selectedSchool, campuses, dispatchFetchCampuses]);

  useLayoutEffect(() => {
    track('View - School page');
  }, []);

  const isInvalid = useMemo(() => {
    if (selectedSchool === null || selectedSchool === undefined) {
      return true;
    }

    const { value: schoolId } = selectedSchool;
    return campuses[schoolId] === undefined;
  }, [selectedSchool, campuses]);

  const handleSubmit = event => {
    event.preventDefault();

    if (isInvalid) {
      return;
    }

    analytics.sendEvent('Click - Next', 'school');

    const hasSingleCampus = selectedSchool.campuses.length === 1;
    const isNotSupported = selectedSchool.supported === false;

    const schoolPayload = payloadFactory.createAddPageDataPayload(
      'school',
      selectedSchool,
      'schoolGuid',
      selectedSchool.value,
    );
    const schoolNamePayload = payloadFactory.createAddPageDataPayload(
      'schoolName',
      selectedSchool.label,
    );
    dispatchAddPageData(schoolPayload);
    dispatchAddPageData(schoolNamePayload);

    if (hasChanged) {
      const clearCampusPayload = payloadFactory.createResetPageDataPayload('campus', 'campusGuid');
      dispatchAddPageData(clearCampusPayload);
    }

    if (hasSingleCampus && !isNotSupported) {
      const { value: schoolId } = selectedSchool;
      const [campus] = campuses[schoolId];
      const campusPayload = payloadFactory.createAddPageDataPayload(
        'campus',
        campus,
        'campusGuid',
        selectedSchool.campuses[0],
      );
      dispatchAddPageData(campusPayload);
    }

    if (isNotSupported) {
      dispatchUpdateUnsupportedSchoolsInterest();
    }
    track('Submit - School page', {
      school_name: selectedSchool.label,
      search_keyword: searchTerm,
    });
    dispatchWizardNav({ dir: 'fwd', resetState: hasChanged });
  };

  const setSchool = school => {
    const { value: selectedId } = selected || {};
    const { value: schoolId } = school || {};
    setHasChanged(selectedId !== schoolId);
    setSelectedSchool(school);
  };

  const helpText = 'Type in the school that you’d like to study at.';

  return (
    <PageLayout title="Which school do you want to go to?" helpText={helpText}>
      <form onSubmit={handleSubmit}>
        <div className="row-spacer">
          <SelectLoadingFetch
            single="school"
            plural="schools"
            onChange={setSchool}
            getTerm={st => setSearchTerm(st)}
            selected={selectedSchool}
            hook={useSchools}
            fuseConfigKeys={['label', 'acronym', 'schoolName', 'universityName']}
            minCharSearchLength={3}
          />
        </div>

        <div className="row-spacer">
          <Button type="submit" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

SchoolPage.propTypes = {
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
  dispatchFetchCampuses: PropTypes.func.isRequired,
  dispatchUpdateUnsupportedSchoolsInterest: PropTypes.func.isRequired,
};

SchoolPage.defaultProps = {};

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
  dispatchFetchCampuses: fetchCampusesRequest,
  dispatchUpdateUnsupportedSchoolsInterest: persistUnsupportedSchoolsInterest,
};

export { SchoolPage };

export default connect(null, mapDispatchToProps)(SchoolPage);
